import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import reportService from '../../../services/reportService';
import { getWarehouses } from '../../../services/warehousesService';
import Toast from '../../../components/Common/Toast';
import InventoryDashboardView from './InventoryDashboardView';
import {
  loadStoredFilters,
  saveStoredFilters,
  buildFilterParams,
} from './shared';

export default function InventoryDashboardPage() {
  const [filters, setFilters] = useState(loadStoredFilters);
  const [warehouses, setWarehouses] = useState([]);
  const [loading, setLoading] = useState(false);
  const [dashboardData, setDashboardData] = useState(null);
  const [dashboardMeta, setDashboardMeta] = useState(null);
  const [toast, setToast] = useState({ isOpen: false, message: '', type: 'info' });

  useEffect(() => {
    getWarehouses({ active_only: true }).then((r) => {
      if (r.success && r.data) setWarehouses(r.data);
    }).catch(() => {});
  }, []);

  const updateFilter = useCallback((key, value) => {
    setFilters((prev) => {
      const next = { ...prev, [key]: value };
      saveStoredFilters(next);
      return next;
    });
  }, []);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await reportService.getInventoryDashboard(buildFilterParams(filters));
      if (res.success) {
        setDashboardData(res.data);
        setDashboardMeta(res.meta);
      } else {
        setToast({ isOpen: true, message: res.message || 'Error al cargar', type: 'error' });
      }
    } catch (error) {
      setToast({
        isOpen: true,
        message: error.response?.data?.message || 'Error al cargar el dashboard',
        type: 'error',
      });
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const empty = dashboardData && (dashboardData.kpis?.products_with_stock || 0) === 0
    && (dashboardData.kpis?.entries_count || 0) === 0
    && (dashboardData.kpis?.exits_count || 0) === 0;

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="p-6">
        <div className="max-w-[1500px] mx-auto">
          <nav className="text-sm text-gray-600 mb-3">
            <Link to="/dashboard" className="hover:text-teal-600">Dashboard</Link>
            <span className="mx-2">/</span>
            <Link to="/warehouses" className="hover:text-teal-600">Almacenes</Link>
            <span className="mx-2">/</span>
            <span className="text-gray-800">Dashboard de inventario</span>
          </nav>

          <div className="mb-4">
            <h1 className="text-3xl font-bold text-gray-800">Dashboard de inventario</h1>
            <p className="text-gray-600 mt-1 max-w-3xl">
              Indicadores de stock, rotación de productos clave, distribución entre almacenes e insumos más relevantes.
            </p>
          </div>

          <div className="bg-white rounded-lg shadow-md p-5 mb-6">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-3 mb-4">
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Desde</label>
                <input
                  type="date"
                  value={filters.dateFrom}
                  onChange={(e) => updateFilter('dateFrom', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Hasta</label>
                <input
                  type="date"
                  value={filters.dateTo}
                  onChange={(e) => updateFilter('dateTo', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Almacén</label>
                <select
                  value={filters.warehouseId}
                  onChange={(e) => updateFilter('warehouseId', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                >
                  <option value="">Todos los almacenes</option>
                  {warehouses.map((w) => (
                    <option key={w.id} value={w.id}>{w.code} — {w.name}</option>
                  ))}
                </select>
              </div>
              <div className="flex items-end">
                <button
                  type="button"
                  onClick={loadData}
                  disabled={loading}
                  className="w-full px-5 py-2 bg-teal-600 hover:bg-teal-700 disabled:opacity-50 text-white rounded-md font-medium text-sm"
                >
                  {loading ? 'Cargando...' : 'Consultar'}
                </button>
              </div>
            </div>
            <div className="flex flex-wrap gap-2 text-sm">
              <Link to="/reports/kardex" className="text-teal-600 hover:underline">Kardex</Link>
              <span className="text-gray-300">·</span>
              <Link to="/reports/entry-change-alerts" className="text-teal-600 hover:underline">Entradas con mayor cambio</Link>
              <span className="text-gray-300">·</span>
              <Link to="/reports/kardex-inconsistencies" className="text-teal-600 hover:underline">Inconsistencias</Link>
              <span className="text-gray-300">·</span>
              <Link to="/reports/entries-exits" className="text-teal-600 hover:underline">Entradas y salidas</Link>
            </div>
          </div>

          {loading && !dashboardData ? (
            <div className="bg-white rounded-lg shadow-md p-12 text-center text-gray-500">
              <p>Cargando indicadores…</p>
              <p className="text-sm text-gray-400 mt-2">Puede tardar unos segundos con todos los almacenes.</p>
            </div>
          ) : !loading && !dashboardData ? (
            <div className="bg-white rounded-lg shadow-md p-10 text-center">
              <p className="text-gray-600 mb-3">No se pudo cargar el dashboard.</p>
              <button
                type="button"
                onClick={loadData}
                className="px-4 py-2 bg-teal-600 hover:bg-teal-700 text-white rounded-md text-sm font-medium"
              >
                Reintentar
              </button>
            </div>
          ) : empty ? (
            <div className="bg-white rounded-lg shadow-md p-10 text-center">
              <p className="text-gray-600 mb-2">No hay movimientos ni stock en el período con los filtros actuales.</p>
              <p className="text-sm text-gray-500">Amplía el rango de fechas o revisa el <Link to="/reports/kardex" className="text-teal-600 hover:underline">Kardex</Link>.</p>
            </div>
          ) : (
            <InventoryDashboardView data={dashboardData} meta={dashboardMeta} />
          )}
        </div>
      </div>

      <Toast
        isOpen={toast.isOpen}
        message={toast.message}
        type={toast.type}
        onClose={() => setToast({ ...toast, isOpen: false })}
      />
    </div>
  );
}
